import React from 'react';
import { Avatar, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Link, NavigateFunction } from 'react-router-dom';
import dayjs from 'dayjs';
import ProposalService from '../../services/ProposalService';
import { formatDateString } from '../../utils';
import AssigneeSummary from './AssigneeSummary';

const { Text } = Typography;

export type ProposalSummaryRow = {
  id: number | string;
  company?: { id?: number; name?: string };
  address?: { state?: string; city?: string; title?: string };
  account?: { firstName?: string; lastName?: string };
  originalPrice?: number;
  createdAt?: string;
  proposalNumber?: string;
  statusId?: number;
  offerAssignee?: { firstName?: string; lastName?: string; email?: string; id?: number };
  planAssignee?: { firstName?: string; lastName?: string; email?: string; id?: number };
  reportAssignee?: { firstName?: string; lastName?: string; email?: string; id?: number };
  invoiceAssignee?: { firstName?: string; lastName?: string; email?: string; id?: number };
  financeAssignee?: { firstName?: string; lastName?: string; email?: string; id?: number };
  legalAssignee?: { firstName?: string; lastName?: string; email?: string; id?: number };
  offerAssigneeUserId?: number;
  planAssigneeUserId?: number;
  reportAssigneeUserId?: number;
  invoiceAssigneeUserId?: number;
  financeAssigneeUserId?: number;
  legalAssigneeUserId?: number;
  validityDate?: string;
};

export const sortByValidityProximity = (list: ProposalSummaryRow[]) => {
  const now = dayjs();
  return [...list].sort((a, b) => {
    const aDiff = a?.validityDate ? Math.abs(dayjs(a.validityDate).diff(now)) : Number.POSITIVE_INFINITY;
    const bDiff = b?.validityDate ? Math.abs(dayjs(b.validityDate).diff(now)) : Number.POSITIVE_INFINITY;
    if (aDiff !== bDiff) {
      return aDiff - bDiff;
    }
    return (a?.validityDate ? dayjs(a.validityDate).valueOf() : 0) - (b?.validityDate ? dayjs(b.validityDate).valueOf() : 0);
  });
};

type FetchParams = {
  statusId: number;
  daysToAdd: number;
  startDate?: string;
  endDate?: string;
};

export const fetchOverdueProposals = async ({ statusId, daysToAdd, startDate, endDate }: FetchParams) => {
  const params: Record<string, any> = {
    company: '',
    statuIds: String(statusId),
    sort: 'createdAt',
    sortField: 'createdAt',
    sortDirection: 'DESC' as const,
    accountId: '',
    companyId: '',
    city: '',
    district: '',
    direction: 'DESC' as const,
  };

  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const response: any = await ProposalService.getAllLight(params);
  const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
  const proposals: ProposalSummaryRow[] = list.map((item: ProposalSummaryRow) => ({
    ...item,
    validityDate: item?.validityDate ?? (item?.createdAt ? dayjs(item.createdAt).add(daysToAdd, 'day').format() : undefined),
  }));

  return sortByValidityProximity(proposals);
};

export const buildOverdueColumns = (navigate: NavigateFunction): ColumnsType<ProposalSummaryRow> => [
  {
    title: 'Firma Adı',
    dataIndex: ['company', 'name'],
    key: 'company',
    sorter: (a, b) => (a?.company?.name || '').localeCompare(b?.company?.name || '', 'tr'),
    render: (text: string, record) => {
      const combinedText = `${record?.address?.state || ''}${record?.address?.state && record?.address?.city ? '/' : ''}${record?.address?.city || ''}`;
      const controlTitle = record?.address?.title;
      const companyId = record?.company?.id;
      const companyName = text || '-';

      return (
        <div className="d-flex align-items-start">
          <div className="avatar-image-logo" style={{ marginRight: 10, marginTop: 4 }}>
            <Avatar
              className="avatar-image-logo"
              style={{ backgroundColor: '#f0f0f0', color: '#000', cursor: companyId ? 'pointer' : 'default' }}
              onClick={() => companyId && navigate(`/companies/${companyId}`)}
            >
              {(companyName || '?').charAt(0).toUpperCase()}
            </Avatar>
          </div>
          <div>
            <Tooltip
              placement="topLeft"
              title={
                <>
                  {companyName.toUpperCase()} <br />
                  {controlTitle && <>{controlTitle}<br /></>}
                  {combinedText}
                </>
              }
            >
              <Text
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  width: '220px',
                  cursor: companyId ? 'pointer' : 'default',
                  fontWeight: 600,
                }}
                onClick={() => companyId && navigate(`/companies/${companyId}`)}
              >
                {companyName.toUpperCase()}
              </Text>
            </Tooltip>
            {controlTitle && (
              <div style={{ color: '#666', fontSize: 12 }}>
                {controlTitle.length > 60 ? (
                  <Tooltip title={controlTitle}>
                    <span>{controlTitle.slice(0, 60)}...</span>
                  </Tooltip>
                ) : (
                  <span>{controlTitle}</span>
                )}
              </div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    title: 'Sorumlu',
    key: 'assignees',
    sorter: (a, b) => {
      const aName = `${a?.offerAssignee?.firstName || ''} ${a?.offerAssignee?.lastName || ''}`.trim();
      const bName = `${b?.offerAssignee?.firstName || ''} ${b?.offerAssignee?.lastName || ''}`.trim();
      return aName.localeCompare(bName, 'tr');
    },
    render: (_text, record) => <AssigneeSummary record={record} statusId={record.statusId} />,
  },
  {
    title: 'Teklif Geçerlilik Tarihi',
    dataIndex: 'validityDate',
    key: 'validityDate',
    defaultSortOrder: 'ascend',
    sorter: (a, b) => {
      const now = dayjs();
      const aDiff = a?.validityDate ? Math.abs(dayjs(a.validityDate).diff(now)) : Number.POSITIVE_INFINITY;
      const bDiff = b?.validityDate ? Math.abs(dayjs(b.validityDate).diff(now)) : Number.POSITIVE_INFINITY;
      if (aDiff !== bDiff) {
        return aDiff - bDiff;
      }
      return dayjs(a.validityDate || 0).valueOf() - dayjs(b.validityDate || 0).valueOf();
    },
    render: (date?: string, record?: ProposalSummaryRow) => {
      if (!date) {
        return '-';
      }

      const createdLabel = record?.createdAt ? formatDateString(record.createdAt) : undefined;

      return (
        <Tooltip title={createdLabel ? `Oluşturulma: ${createdLabel}` : undefined}>
          <span>{formatDateString(date)}</span>
        </Tooltip>
      );
    },
  },
  {
    title: 'Teklif No',
    dataIndex: 'proposalNumber',
    key: 'proposalNumber',
    sorter: (a, b) => (a.proposalNumber || '').localeCompare(b.proposalNumber || '', 'tr'),
    render: (value?: string) =>
      value ? (
        <Link to={`/proposals?search=${encodeURIComponent(value)}`} style={{ color: '#2563eb', fontWeight: 500 }}>
          {value}
        </Link>
      ) : (
        '-'
      ),
  },
];

